// 统一用户设置存储层（settings 集合）：quickNavs / 藏宝阁比例 / 物价配置 合为一个对象，
// 按用户隔离。支持两种后端：
//   - local:    uni 跨端存储（uni.setStorageSync），作为默认/离线回退
//   - unicloud: 每个用户一个文档（_id = userId），集合名可配
// 说明：VITE_STORAGE_MODE=indexeddb / localstorage / uni 均按 local 处理（settings 不用 IndexedDB）；
//       仅 unicloud 走云端。后续接入 uni-id 时，把 getCurrentUserId() 换成真实 uid 即可。
import { ref } from 'vue'

const MODE = (import.meta.env.VITE_STORAGE_MODE || '').trim().toLowerCase()
const COLLECTION = (import.meta.env.VITE_UNICLOUD_SETTINGS_COLLECTION || 'mhxy_toolkit_settings').trim() || 'mhxy_toolkit_settings'

function resolveMode() {
  // settings 不使用 indexeddb；只有明确 unicloud 才走云端
  if (MODE === 'unicloud') return 'unicloud'
  return 'local'
}
const mode = resolveMode()

const LS_PREFIX = 'mhxy-settings-'

// 当前用户：匿名设备 id 占位，结构已按每用户隔离，后续可替换为 uni-id 的 uid
function getCurrentUserId() {
  try {
    let uid = uni.getStorageSync('mhxy_uid')
    if (!uid) {
      uid = 'u_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      uni.setStorageSync('mhxy_uid', uid)
    }
    return uid
  } catch (e) {
    return 'anonymous'
  }
}

function defaultSettings() {
  return {
    quickNavs: [],
    cbg: { rmb: 200, coinWan: 3000 },
    price: { version: 2, overrides: {} },
    homeCards: null,
    roleLevel: null,
    runAssist: defaultRunAssist()
  }
}

function defaultRunAssist() {
  return { mergeSanyao: false, mergeYueqi: false, mergeHuahui: false }
}
function mergeRunAssist(r) {
  return {
    mergeSanyao: !!(r && r.mergeSanyao),
    mergeYueqi: !!(r && r.mergeYueqi),
    mergeHuahui: !!(r && r.mergeHuahui)
  }
}

// 价格单位由「万两」迁移为「两」后，版本不符的覆盖值（万两量级）已不兼容，需重置
function mergePrice(p) {
  if (p && p.version === PRICE_VERSION) return { version: PRICE_VERSION, overrides: p.overrides || {} }
  return { version: PRICE_VERSION, overrides: {} }
}
function mergeCbg(c) {
  return { rmb: (c && c.rmb != null) ? c.rmb : 200, coinWan: (c && c.coinWan != null) ? c.coinWan : 3000 }
}
const PRICE_VERSION = 2

const cache = ref(defaultSettings())
let loaded = false

async function loadSettings() {
  if (loaded) return cache.value
  loaded = true
  const uid = getCurrentUserId()
  try {
    if (mode === 'unicloud') {
      const doc = await ucGet(uid)
      if (doc) {
        cache.value = {
          quickNavs: Array.isArray(doc.quickNavs) ? doc.quickNavs : [],
          cbg: mergeCbg(doc.cbg),
          price: mergePrice(doc.price),
          homeCards: (doc && Array.isArray(doc.homeCards)) ? doc.homeCards : null,
          roleLevel: (doc && doc.roleLevel != null) ? doc.roleLevel : null,
          runAssist: mergeRunAssist(doc.runAssist)
        }
      }
    } else {
      const v = uni.getStorageSync(LS_PREFIX + uid)
      if (v && typeof v === 'object') {
        cache.value = {
          quickNavs: Array.isArray(v.quickNavs) ? v.quickNavs : [],
          cbg: mergeCbg(v.cbg),
          price: mergePrice(v.price),
          homeCards: (v && Array.isArray(v.homeCards)) ? v.homeCards : null,
          roleLevel: (v && v.roleLevel != null) ? v.roleLevel : null,
          runAssist: mergeRunAssist(v.runAssist)
        }
      }
    }
  } catch (e) {
    console.warn('[settingsStore] 读取失败，使用默认', e)
  }
  return cache.value
}

function saveSettings() {
  const uid = getCurrentUserId()
  try {
    if (mode === 'unicloud') ucUpsert(uid, cache.value)
    else uni.setStorageSync(LS_PREFIX + uid, cache.value)
  } catch (e) {
    console.warn('[settingsStore] 保存失败', e)
  }
}

// 仅写本地缓存（导入场景：按需求只落本地，不推云端）
function persistLocal() {
  const uid = getCurrentUserId()
  try { uni.setStorageSync(LS_PREFIX + uid, cache.value) } catch (e) {}
}

// ============ uniCloud ============
async function ucGet(uid) {
  const db = uniCloud.database()
  const res = await db.collection(COLLECTION).doc(uid).get()
  const arr = res && (res.result && res.result.data) || res.data
  return Array.isArray(arr) && arr.length ? arr[0] : null
}
function ucUpsert(uid, data) {
  try {
    const db = uniCloud.database()
    db.collection(COLLECTION).doc(uid).set({ ...data, _id: uid })
      .catch(e => console.warn('[settingsStore] 云端保存失败', e))
  } catch (e) {
    console.warn('[settingsStore] 云端保存失败', e)
  }
}

export function useSettingsStore() {
  return { cache, loadSettings, saveSettings, persistLocal, mode, getCurrentUserId }
}
export { cache, loadSettings, saveSettings, persistLocal, mode, getCurrentUserId }

// 预加载（local 同步生效；unicloud 异步后由 reactive 自动刷新界面）
loadSettings()
